import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { allProducts } from '../../mocks/products';

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 3000]);
  const [sortBy, setSortBy] = useState<string>('featured');

  const categories = ['All', 'Gaming Consoles', 'Laptops', 'Desktop PCs', 'Accessories'];

  // Read category from URL parameter
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && categories.includes(categoryParam)) {
      setSelectedCategory(categoryParam);
    } else {
      setSelectedCategory('All');
    }
  }, [searchParams]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    if (category === 'All') {
      navigate('/products');
    } else {
      navigate(`/products?category=${encodeURIComponent(category)}`);
    }
  };

  const filteredProducts = allProducts
    .filter(product => 
      (selectedCategory === 'All' || product.category === selectedCategory) &&
      product.price >= priceRange[0] && product.price <= priceRange[1]
    )
    .sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });

  const addToCart = (product: any) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find((item: any) => item.id === product.id);
    
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <h1 className="text-5xl font-black text-gray-900 mb-4">Our Products</h1>
            <p className="text-lg text-gray-600">Explore our complete collection of gaming and computing products</p>
          </div>

          <div className="flex gap-8">
            {/* Sidebar Filters */}
            <aside className="w-64 flex-shrink-0">
              <div className="sticky top-24 space-y-8">
                {/* Category Filter */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4 pb-3 border-b border-gray-200">
                    Category
                  </h3>
                  <div className="space-y-3">
                    {categories.map((category) => (
                      <label key={category} className="flex items-center gap-3 cursor-pointer group">
                        <input
                          type="radio"
                          name="category"
                          checked={selectedCategory === category}
                          onChange={() => handleCategoryChange(category)}
                          className="w-4 h-4 text-[#FF6B6B] cursor-pointer"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-[#FF6B6B] transition-colors">
                          {category}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4 pb-3 border-b border-gray-200">
                    Price Range
                  </h3>
                  <div className="space-y-4">
                    <input
                      type="range"
                      min="0"
                      max="3000"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                      className="w-full cursor-pointer"
                    />
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                  </div>
                </div>

                {/* Stock Filter */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 mb-4 pb-3 border-b border-gray-200">
                    Availability
                  </h3>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="w-4 h-4 text-[#FF6B6B] cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-[#FF6B6B] transition-colors">
                      In Stock Only
                    </span>
                  </label>
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-8 bg-gray-50 px-6 py-4 rounded-xl">
                <p className="text-sm text-gray-600">
                  Showing <strong>{filteredProducts.length}</strong> products
                </p>
                <div className="flex items-center gap-4">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF6B6B]"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                  </select>
                  <div className="flex gap-2">
                    <button className="w-10 h-10 flex items-center justify-center bg-[#FF6B6B] text-white rounded-lg cursor-pointer">
                      <i className="ri-grid-fill"></i>
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 cursor-pointer">
                      <i className="ri-list-check"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Products */}
              <div className="grid grid-cols-3 gap-8" data-product-shop>
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 group flex flex-col"
                  >
                    <Link to={`/product/${product.id}`} className="block">
                      <div className="aspect-square bg-gray-50 overflow-hidden relative">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        {product.inStock && (
                          <span className="absolute top-4 right-4 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-semibold whitespace-nowrap">
                            In Stock
                          </span>
                        )}
                      </div>
                      <div className="p-6">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                          {product.category}
                        </p>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{product.name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{product.specs}</p>
                        <div className="flex items-center gap-1 mb-4">
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-yellow-400 text-sm`}
                            ></i>
                          ))}
                          <span className="text-xs text-gray-500 ml-2">({product.rating})</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-3xl font-black text-[#FF6B6B]">
                            ${product.price}
                          </span>
                        </div>
                      </div>
                    </Link>
                    <div className="px-6 pb-6 mt-auto">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          addToCart(product);
                        }}
                        className="w-full py-3 bg-black text-white text-sm font-semibold rounded-xl hover:bg-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
